import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";

// ✅ Validation patterns centralized
const validationPatterns = {
  decimal: {
    pattern: /^\d*\.?\d{0,2}$/,
    message: "Please enter a valid decimal number with up to 2 decimal places",
  },
  numberonly: {
    pattern: /^\d*$/,
    message: "Please enter numbers only",
  },
  numberwithslash: {
    pattern: /^[0-9/]*$/,
    message: "Please enter numbers and slashes only",
  },
  url: {
    pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    message:
      "Please enter a valid URL slug (lowercase letters, numbers, and hyphens only, no consecutive hyphens)",
  },
  alphaonly: {
    pattern: /^[A-Za-z\s]*$/,
    message: "Please enter letters only",
  },
  alphanumeric: {
    pattern: /^[A-Za-z0-9\s]*$/,
    message: "Please enter letters and numbers only",
  },
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: "Please enter a valid email address",
  },
  phone:{
    pattern: /^\d{10,15}$/,
    message:"Please enter 10-15 digit phone number",
  },
  percentage: {
    pattern: /^(?:100(?:\.00?)?|\d{1,2}(?:\.\d{1,2})?)$/,
    message:
      "Please enter a valid percentage (0.00 to 100.00 with up to 2 decimal places)",
  },
  pathorurl: {
    pattern: /^\/?[A-Za-z0-9/_-]*$/,
    message:
      "Only letters, numbers, slashes (/), underscores (_), and dashes (-) are allowed (optionally starting with /)",
  },
};

// ✅ Helper: Convert text to a slug
const convertToSlug = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

// ✅ InputField component
export const InputField = forwardRef(
  (
    {
      label,
      id,
      type = "text",
      value,
      onChange,
      placeholder,
      maxLength,
      minLength,
      required = false,
      showError = true,
      validationType,
    },
    ref
  ) => {
    const [error, setError] = useState("");
    const [internalValue, setInternalValue] = useState(value || "");
    const [hasInteracted, setHasInteracted] = useState(false);

    // ✅ Validation logic
    const validateInput = useCallback(
      (inputValue) => {
        if (!inputValue && required) return "This field is required";
        if (!inputValue) return "";

        if (minLength && inputValue.length < minLength)
          return `Minimum ${minLength} characters required`;
        if (maxLength && inputValue.length > maxLength)
          return `Maximum ${maxLength} characters allowed`;

        if (validationType && validationPatterns[validationType]) {
          const { pattern, message } = validationPatterns[validationType];
          if (!pattern.test(inputValue)) return message;
        }

        if (validationType === "percentage") {
          const numValue = parseFloat(inputValue);
          if (isNaN(numValue) || numValue < 0 || numValue > 100)
            return "Percentage must be between 0 and 100";
        }

        return "";
      },
      [required, maxLength, minLength, validationType]
    );

    // ✅ Expose methods to parent
    useImperativeHandle(
      ref,
      () => ({
        validate: () => {
          const validationError = validateInput(internalValue);
          setError(validationError);
          return validationError;
        },
        touch: () => setHasInteracted(true),
        getValue: () => internalValue,
        getError: () => error,
        isRequired: () => required,
        reset: () => {
          setInternalValue("");
          setError("");
          setHasInteracted(false);
        },
      }),
      [validateInput, internalValue, error, required]
    );

    // ✅ Handle value changes
    const handleChange = (e) => {
      let newValue = e.target.value;
      if (validationType === "url") newValue = convertToSlug(newValue);

      const validationError = validateInput(newValue);
      if (!hasInteracted) setHasInteracted(true);

      setInternalValue(newValue);
      setError(validationError);

      if (onChange) onChange({ target: { id: e.target.id, value: newValue } });
    };

    // ✅ Handle paste
    const handlePaste = (e) => {
      let pastedData = e.clipboardData.getData("text");
      if (validationType === "url") pastedData = convertToSlug(pastedData);

      const validationError = validateInput(pastedData);
      if (!hasInteracted) setHasInteracted(true);

      if (validationError) {
        e.preventDefault();
        setError(validationError);
      } else {
        e.preventDefault();
        setInternalValue(pastedData);
        if (onChange)
          onChange({ target: { id: e.target.id, value: pastedData } });
      }
    };

    // ✅ Handle blur (mark as touched)
    const handleBlur = () => {
      if (!hasInteracted) setHasInteracted(true);
      const validationError = validateInput(internalValue);
      setError(validationError);
    };

    // ✅ Handle keypress (all validations)
    const handleKeyPress = (e) => {
      // 🔹 Number only
      if (
        validationType === "numberonly" &&
        !/[0-9]/.test(e.key) &&
        !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
          e.key
        )
      )
        e.preventDefault();

      // 🔹 Number with slash (fixed ✅)
      if (validationType === "pathorurl") {
        const allowedKeys = [
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "Tab",
          "Slash",
          "/",
          "-",
        ];

        if (!/[A-Za-z0-9/-]/.test(e.key) && !allowedKeys.includes(e.key)) {
          e.preventDefault();
        }
      }

      // 🔹 Alpha only
      if (
        validationType === "alphaonly" &&
        !/[A-Za-z\s]/.test(e.key) &&
        !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
          e.key
        )
      )
        e.preventDefault();

      // 🔹 Alphanumeric
      if (
        validationType === "alphanumeric" &&
        !/[A-Za-z0-9\s]/.test(e.key) &&
        !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
          e.key
        )
      )
        e.preventDefault();

      // 🔹 Decimal
      if (validationType === "decimal") {
        const currentValue = e.target.value;
        if (e.key === "." && currentValue.includes(".")) e.preventDefault();

        if (currentValue.includes(".")) {
          const decimalPart = currentValue.split(".")[1];
          if (
            decimalPart &&
            decimalPart.length >= 2 &&
            !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
              e.key
            )
          )
            e.preventDefault();
        }

        if (
          !/[0-9.]/.test(e.key) &&
          !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
            e.key
          )
        )
          e.preventDefault();
      }

      // 🔹 Percentage
      if (validationType === "percentage") {
        const currentValue = e.target.value;

        if (e.key === "." && currentValue.includes(".")) {
          e.preventDefault();
          return;
        }

        const decimalPart = currentValue.split(".")[1];
        if (
          decimalPart &&
          decimalPart.length >= 2 &&
          !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
            e.key
          )
        ) {
          e.preventDefault();
          return;
        }

        if (
          !/[0-9.]/.test(e.key) &&
          !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
            e.key
          )
        ) {
          e.preventDefault();
          return;
        }

        const newValue = currentValue + e.key;
        const numValue = parseFloat(newValue);
        if (!isNaN(numValue) && numValue > 100) e.preventDefault();
      }
    };

    // ✅ Sync external -> internal value
    useEffect(() => {
      setInternalValue(value || "");
    }, [value]);

    // ✅ Auto-validate on change (after interaction)
    useEffect(() => {
      if (hasInteracted) {
        const validationError = validateInput(internalValue);
        setError(validationError);
      }
    }, [internalValue, validateInput, hasInteracted]);

    return (
      <div className="mb-3">
        <label htmlFor={id} className="form-label">
          {label} {required && <span className="required-field">*</span>}
        </label>
        <input
          type={type}
          className={`form-control ${required ? "required" : ""} ${
            error && hasInteracted ? "is-invalid" : ""
          }`}
          id={id}
          placeholder={placeholder}
          value={internalValue}
          onChange={handleChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          maxLength={maxLength}
          minLength={minLength}
        />
        {showError && error && hasInteracted && (
          <div className="invalid-feedback">{error}</div>
        )}
      </div>
    );
  }
);

InputField.displayName = "InputField";
